import {
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  CloseRounded,
  DragIndicator,
  SearchRounded,
} from '@mui/icons-material';
import {
  Box,
  Button as MuiButton,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import { PinCandidate } from '../../perps-v1-shared/types';

interface SelectedRowData {
  symbol: string;
  label: string;
  dex: string;
  isListed: boolean;
}

function SelectedRow({
  row,
  index,
  count,
  orderable,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  row: SelectedRowData;
  index: number;
  count: number;
  orderable: boolean;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onRemove: (symbol: string) => void;
}) {
  const translate = useTranslate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.symbol,
    disabled: !orderable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          px: 1.25,
          py: 1,
          border: '1px solid',
          borderRadius: 1.5,
          borderColor: row.isListed ? 'divider' : 'warning.main',
        }}
      >
        {orderable ? (
          <Typography
            variant="subtitle2"
            sx={{ width: 20, textAlign: 'center', color: 'primary.main' }}
          >
            {index + 1}
          </Typography>
        ) : null}
        {orderable ? (
          <IconButton
            size="small"
            sx={{ cursor: 'grab', color: 'text.disabled' }}
            {...attributes}
            {...listeners}
          >
            <DragIndicator fontSize="small" />
          </IconButton>
        ) : null}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {row.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.dex ? `${row.symbol} · ${row.dex}` : row.symbol}
          </Typography>
        </Box>
        {!row.isListed ? (
          <Chip size="small" color="warning" label={translate('module.perpsV1Views.membersDelisted')} />
        ) : null}
        <Stack direction="row" spacing={0}>
          {orderable ? (
            <>
              <IconButton
                size="small"
                disabled={index === 0}
                onClick={() => onMoveUp(index)}
                aria-label={translate('module.perpsV1Views.membersMoveUp')}
              >
                <ArrowUpwardRounded fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={index === count - 1}
                onClick={() => onMoveDown(index)}
                aria-label={translate('module.perpsV1Views.membersMoveDown')}
              >
                <ArrowDownwardRounded fontSize="small" />
              </IconButton>
            </>
          ) : null}
          <IconButton
            size="small"
            onClick={() => onRemove(row.symbol)}
            aria-label={translate('module.perpsV1Views.membersRemove')}
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </div>
  );
}

function CandidateRow({
  candidate,
  onAdd,
}: {
  candidate: PinCandidate;
  onAdd: (symbol: string) => void;
}) {
  const translate = useTranslate();
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ px: 1.25, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap>
          {candidate.displaySymbol}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {candidate.dex ? `${candidate.symbol} · ${candidate.dex}` : candidate.symbol}
        </Typography>
      </Box>
      <MuiButton size="small" variant="outlined" color="success" onClick={() => onAdd(candidate.symbol)}>
        {translate('module.perpsV1Views.membersAdd')}
      </MuiButton>
    </Stack>
  );
}

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  candidates: PinCandidate[];
  loading: boolean;
  // 候选池不可用（加载中或拉取失败）时无从判断已配置 symbol 是否下架，
  // 一律不打「已下架」标记，避免把「拉取失败」误报成「币种已下架」。
  candidatesUnavailable: boolean;
  orderable: boolean;
  labelKey: string;
  emptyKey: string;
  // Extra symbols to hide from the left (candidate) column on top of `value` itself — the
  // dialog passes the OTHER list here (include picker gets excludeSymbols, exclude picker gets
  // includeSymbols), because a symbol configured in both lists is a contradictory config the
  // backend resolves as exclude-wins, so it should not be offered as a fresh pick from either side.
  unavailableSymbols?: string[];
}

// Embeddable dual-list transfer picker used inside ViewSymbolsDialog for both the include list
// (orderable=true, order is the app-visible display order) and the exclude list
// (orderable=false, membership only). A symbol already in `value` (or in `unavailableSymbols`)
// never appears in the left/candidate column — it can only be picked once, and only from one
// list. A symbol in `value` but absent from `candidates` (delisted since it was configured)
// still renders on the right — flagged, removable, never silently dropped.
export default function ViewMembersEditor({
  value,
  onChange,
  candidates,
  loading,
  candidatesUnavailable,
  orderable,
  labelKey,
  emptyKey,
  unavailableSymbols,
}: Props) {
  const translate = useTranslate();
  const [keyword, setKeyword] = useState('');

  const candidateMap = useMemo(() => new Map(candidates.map((c) => [c.symbol, c])), [candidates]);
  const selectedSet = useMemo(() => new Set(value), [value]);
  const unavailableSet = useMemo(() => new Set(unavailableSymbols ?? []), [unavailableSymbols]);

  // Candidate pool with already-selected and cross-list symbols removed — a symbol leaves this
  // column the moment it's chosen, so there is no "add this twice" state to guard against.
  const availableCandidates = useMemo(
    () => candidates.filter((c) => !selectedSet.has(c.symbol) && !unavailableSet.has(c.symbol)),
    [candidates, selectedSet, unavailableSet]
  );

  const filteredCandidates = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    if (!key) return availableCandidates;
    return availableCandidates.filter(
      (c) => c.symbol.toLowerCase().includes(key) || c.displaySymbol.toLowerCase().includes(key)
    );
  }, [availableCandidates, keyword]);

  const rows: SelectedRowData[] = useMemo(
    () =>
      value.map((symbol) => {
        const candidate = candidateMap.get(symbol);
        return {
          symbol,
          label: candidate?.displaySymbol ?? symbol,
          dex: candidate?.dex ?? '',
          isListed: candidatesUnavailable ? true : !!candidate,
        };
      }),
    [value, candidateMap, candidatesUnavailable]
  );

  const handleAdd = (symbol: string) => {
    if (selectedSet.has(symbol)) return;
    onChange([...value, symbol]);
  };

  const handleRemove = (symbol: string) => {
    onChange(value.filter((s) => s !== symbol));
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const next = index + direction;
    if (next < 0 || next >= value.length) return;
    onChange(arrayMove(value, index, next));
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    if (!orderable) return;
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = value.indexOf(`${active.id}`);
      const newIndex = value.indexOf(`${over.id}`);
      if (oldIndex < 0 || newIndex < 0) return;
      onChange(arrayMove(value, oldIndex, newIndex));
    }
  };

  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
      <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {translate('module.perpsV1Views.membersAvailable', { count: availableCandidates.length })}
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          placeholder={translate('module.perpsV1Views.membersSearch')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />
        <Box sx={{ height: 220, position: 'relative' }}>
          <Stack spacing={0} sx={{ position: 'absolute', inset: 0, overflowY: 'auto' }}>
            {loading ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {translate('module.perpsV1Views.membersLoading')}
              </Typography>
            ) : filteredCandidates.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {translate('module.perpsV1Views.membersNoResult')}
              </Typography>
            ) : (
              filteredCandidates.map((candidate) => (
                <CandidateRow key={candidate.symbol} candidate={candidate} onAdd={handleAdd} />
              ))
            )}
          </Stack>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {translate(labelKey, { count: value.length })}
        </Typography>
        <Box sx={{ height: 220, position: 'relative' }}>
          <Stack spacing={1} sx={{ position: 'absolute', inset: 0, overflowY: 'auto', pr: 0.5 }}>
            {rows.length === 0 ? (
              <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {translate(emptyKey)}
                </Typography>
              </Box>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={value} strategy={verticalListSortingStrategy}>
                  <Stack spacing={1}>
                    {rows.map((row, index) => (
                      <SelectedRow
                        key={row.symbol}
                        row={row}
                        index={index}
                        count={rows.length}
                        orderable={orderable}
                        onMoveUp={(i) => handleMove(i, -1)}
                        onMoveDown={(i) => handleMove(i, 1)}
                        onRemove={handleRemove}
                      />
                    ))}
                  </Stack>
                </SortableContext>
              </DndContext>
            )}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}
